import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, parseISO, isSameMonth, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  RefreshCw, Link2, Unlink, Check, X, Clock, User, Phone, 
  Mail, Bike, AlertCircle, Loader2, ExternalLink, CheckCircle2, Upload, FileText
} from 'lucide-react';
import LoadingOverlay from '@/components/LoadingOverlay';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OutlookCalendarView = ({ token, onBookingUpdate, refreshTrigger }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Event detail dialog
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  
  // Action dialogs
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Complete (FATTO) dialog state
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [bollaFile, setBollaFile] = useState(null);
  const [bollaPreview, setBollaPreview] = useState(null);
  const [lavoriEffettuati, setLavoriEffettuati] = useState('');
  const [noteNegozio, setNoteNegozio] = useState('');
  const [isUploadingBolla, setIsUploadingBolla] = useState(false);

  // Check connection status
  const checkConnection = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/outlook/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsConnected(response.data.connected);
      setConnectedEmail(response.data.email || '');
    } catch (error) {
      console.error('Error checking Outlook connection:', error);
      setIsConnected(false);
    }
  }, [token]);

  // Fetch events for current month
  const fetchEvents = useCallback(async () => {
    if (!isConnected) return;
    
    setIsLoading(true);
    try {
      // Get the full range visible in the month view (including days from prev/next month)
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      
      const response = await axios.get(`${API_URL}/outlook/calendar/events`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: format(calendarStart, 'yyyy-MM-dd'),
          end_date: format(calendarEnd, 'yyyy-MM-dd')
        }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Errore nel caricamento eventi');
    } finally {
      setIsLoading(false);
    }
  }, [token, isConnected, currentMonth]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  useEffect(() => {
    if (isConnected) {
      fetchEvents();
    } else {
      setIsLoading(false);
    }
  }, [isConnected, fetchEvents]);

  // Refresh events when refreshTrigger changes (e.g., after booking creation)
  useEffect(() => {
    if (refreshTrigger && isConnected) {
      fetchEvents();
    }
  }, [refreshTrigger, isConnected, fetchEvents]);

  // Handle URL params for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('outlook_connected') === 'true') {
      toast.success('Outlook Calendar connesso con successo!');
      checkConnection();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('outlook_error')) {
      toast.error(`Errore: ${params.get('outlook_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkConnection]);

  const handleConnect = async () => {
    try {
      const response = await axios.get(`${API_URL}/outlook/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Errore nella connessione a Outlook');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Sei sicuro di voler disconnettere Outlook Calendar?')) return;
    
    try {
      await axios.post(`${API_URL}/outlook/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsConnected(false);
      setConnectedEmail('');
      setEvents([]);
      toast.success('Outlook Calendar disconnesso');
    } catch (error) {
      toast.error('Errore nella disconnessione');
    }
  };

  // Toggle bidirectional sync - Not supported in Outlook (removed)
  // Outlook Calendar now uses simple one-way sync: app creates events in Outlook
  // No bidirectional webhooks needed

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => 
      direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1)
    );
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const openEventDialog = (event) => {
    setSelectedEvent(event);
    setIsEventDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedEvent) return;
    
    setIsProcessing(true);
    try {
      await axios.put(`${API_URL}/outlook/calendar/events/${selectedEvent.id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { action: 'confirm' }
      });
      toast.success('Prenotazione confermata!');
      setIsEventDialogOpen(false);
      fetchEvents();
      // Refresh bookings in parent dashboard
      if (onBookingUpdate) onBookingUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conferma');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedEvent) return;
    
    setIsProcessing(true);
    try {
      await axios.put(`${API_URL}/outlook/calendar/events/${selectedEvent.id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { action: 'cancel', motivo: cancelReason }
      });
      toast.success('Prenotazione annullata');
      setIsCancelDialogOpen(false);
      setIsEventDialogOpen(false);
      setCancelReason('');
      fetchEvents();
      // Refresh bookings in parent dashboard
      if (onBookingUpdate) onBookingUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedEvent || !newDate || !newStartTime || !newEndTime) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    setIsProcessing(true);
    try {
      await axios.put(`${API_URL}/outlook/calendar/events/${selectedEvent.id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          action: 'reschedule',
          new_date: newDate,
          new_start_time: newStartTime,
          new_end_time: newEndTime
        }
      });
      toast.success('Prenotazione riprogrammata! Email inviata al cliente.');
      setIsRescheduleDialogOpen(false);
      setIsEventDialogOpen(false);
      setNewDate('');
      setNewStartTime('');
      setNewEndTime('');
      fetchEvents();
      // Refresh bookings in parent dashboard
      if (onBookingUpdate) onBookingUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riprogrammazione');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle bolla file selection
  const handleBollaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBollaFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBollaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle complete (FATTO) action
  const handleComplete = async () => {
    if (!selectedEvent) return;
    
    const body = getEventBody(selectedEvent);
    const info = parseEventDescription(body);
    if (!info.bookingId) {
      toast.error('Impossibile completare: prenotazione non trovata');
      return;
    }
    
    setIsUploadingBolla(true);
    try {
      let bollaBase64 = null;
      if (bollaFile) {
        const reader = new FileReader();
        bollaBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(bollaFile);
        });
      }
      
      // Call the booking status update endpoint
      await axios.put(`${API_URL}/bookings/${info.bookingId}/status`, {
        status: 'completed',
        lavori_effettuati: lavoriEffettuati || null,
        note_negozio: noteNegozio || null,
        foto_bolla_base64: bollaBase64
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tagliando completato! Email inviata al cliente.');
      setIsCompleteDialogOpen(false);
      setIsEventDialogOpen(false);
      setBollaFile(null);
      setBollaPreview(null);
      setLavoriEffettuati('');
      setNoteNegozio('');
      fetchEvents();
      // Refresh bookings in parent dashboard
      if (onBookingUpdate) onBookingUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel completamento');
    } finally {
      setIsUploadingBolla(false);
    }
  };

  const openCompleteDialog = () => {
    setBollaFile(null);
    setBollaPreview(null);
    setLavoriEffettuati('');
    setNoteNegozio('');
    setIsCompleteDialogOpen(true);
  };

  // Parse event to get booking info
  const parseEventDescription = (bodyContent) => {
    if (!bodyContent) return {};
    // Handle HTML content - replace <br> with special marker, strip other tags, then split
    let cleanDesc = bodyContent
      .replace(/<br\s*\/?>/gi, '|||')  // Replace <br> with marker
      .replace(/<[^>]*>/g, '')          // Remove other HTML tags
      .replace(/&nbsp;/g, ' ')          // Replace &nbsp;
      .replace(/\r\n/g, '\n');          // Normalize line endings
    
    const lines = cleanDesc.split('|||').map(l => l.trim()).filter(l => l);
    const info = {};
    lines.forEach(line => {
      if (line.includes('Cliente:')) info.cliente = line.split('Cliente:')[1]?.trim();
      if (line.includes('Telefono:')) info.telefono = line.split('Telefono:')[1]?.trim();
      if (line.includes('Email:')) info.email = line.split('Email:')[1]?.trim();
      if (line.includes('Modello:')) info.modello = line.split('Modello:')[1]?.trim();
      if (line.includes('KM:') || line.includes('Km:')) info.km = (line.split('KM:')[1] || line.split('Km:')[1])?.trim();
      if (line.includes('Note:')) info.note = line.split('Note:')[1]?.trim();
      if (line.includes('ID Prenotazione:')) info.bookingId = line.split('ID Prenotazione:')[1]?.trim();
      if (line.includes('Consegna:')) info.consegna = line.split('Consegna:')[1]?.trim();
      if (line.includes('Ritiro:')) info.ritiro = line.split('Ritiro:')[1]?.trim();
    });
    return info;
  };

  // Get event title - support both Outlook (subject) and Google (summary)
  const getEventTitle = (event) => event?.subject || event?.summary || 'Evento';
  
  // Get event body/description - support both Outlook (body) and Google (description)
  const getEventBody = (event) => event?.body || event?.description || '';
  
  // Get event start date - support Outlook format (string) and Google format (object)
  const getEventStart = (event) => event?.start || '';
  
  // Get event end date
  const getEventEnd = (event) => event?.end || '';
  
  // Get web link - support Outlook (webLink) and Google (htmlLink)
  const getWebLink = (event) => event?.webLink || event?.htmlLink || '';

  // Get event status badge color
  const getStatusBadge = (eventOrTitle) => {
    const title = typeof eventOrTitle === 'string' ? eventOrTitle : getEventTitle(eventOrTitle);
    if (title?.includes('CONFERMATO')) {
      return <Badge className="bg-green-500">Confermato</Badge>;
    } else if (title?.includes('ATTESA')) {
      return <Badge className="bg-amber-500">In Attesa</Badge>;
    } else if (title?.includes('COMPLETATO') || title?.includes('FATTO')) {
      return <Badge className="bg-blue-500">Completato</Badge>;
    } else if (title?.includes('ANNULLATO')) {
      return <Badge className="bg-red-500">Annullato</Badge>;
    }
    return <Badge className="bg-purple-500">Evento</Badge>;
  };

  // Not connected view
  if (!isConnected) {
    return (
      <Card className="bg-card border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" />
            Outlook Calendar
          </CardTitle>
          <CardDescription>
            Connetti Outlook Calendar per visualizzare e gestire le prenotazioni
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <CalendarIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Calendario non connesso</h3>
            <p className="text-muted-foreground mb-6">
              Connetti il tuo account Outlook per sincronizzare le prenotazioni con Outlook Calendar
            </p>
            <Button onClick={handleConnect} className="bg-primary">
              <Link2 className="w-4 h-4 mr-2" />
              Connetti Outlook Calendar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                Outlook Calendar
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Check className="w-4 h-4 text-green-500" />
                Connesso a: {connectedEmail}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchEvents} disabled={isLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-400 hover:text-red-300">
                <Unlink className="w-4 h-4 mr-2" />
                Disconnetti
              </Button>
            </div>
          </div>
          
          {/* Month navigation */}
          <div className="flex items-center justify-between mt-4 bg-muted rounded-lg p-2">
            <Button variant="ghost" size="sm" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="font-medium text-lg capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: it })}
              </span>
              <Button variant="ghost" size="xs" onClick={goToToday} className="text-xs">
                Oggi
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigateMonth('next')}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Monthly Calendar Grid */}
              {(() => {
                // Generate calendar days
                const monthStart = startOfMonth(currentMonth);
                const monthEnd = endOfMonth(currentMonth);
                const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
                
                const days = [];
                let day = calendarStart;
                while (day <= calendarEnd) {
                  days.push(day);
                  day = addDays(day, 1);
                }
                
                // Get events for a specific day
                const getEventsForDay = (targetDate) => {
                  const targetDateStr = format(targetDate, 'yyyy-MM-dd');
                  return events.filter(event => {
                    // Support both Outlook format (event.start string) and Google format (event.start.dateTime)
                    const eventStart = event.start?.dateTime || event.start || '';
                    if (!eventStart) return false;
                    const eventDate = eventStart.split('T')[0];
                    return eventDate === targetDateStr;
                  });
                };
                
                // Parse event description/body for booking info
                const parseEventDesc = (description) => {
                  if (!description) return {};
                  // Handle HTML content - replace <br> with special marker, strip other tags
                  let cleanDesc = description
                    .replace(/<br\s*\/?>/gi, '|||')
                    .replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/g, ' ');
                  
                  const lines = cleanDesc.split('|||').map(l => l.trim()).filter(l => l);
                  const info = {};
                  lines.forEach(line => {
                    if (line.includes('Cliente:')) info.cliente = line.split('Cliente:')[1]?.trim();
                    if (line.includes('Modello:')) info.modello = line.split('Modello:')[1]?.trim();
                    if (line.includes('KM:') || line.includes('Km:')) info.km = (line.split('KM:')[1] || line.split('Km:')[1])?.trim();
                  });
                  return info;
                };
                
                // Get event title - support both Outlook (subject) and Google (summary)
                const getEventTitle = (event) => event.subject || event.summary || 'Evento';
                
                // Get event body/description
                const getEventBody = (event) => event.body || event.description || '';
                
                return (
                  <div>
                    {/* Days of week header */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(dayName => (
                        <div key={dayName} className="p-2 text-center text-xs font-medium text-muted-foreground bg-muted rounded">
                          {dayName}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {days.map(calDay => {
                        const dayEvents = getEventsForDay(calDay);
                        const isToday = isSameDay(calDay, new Date());
                        const isCurrentMonth = isSameMonth(calDay, currentMonth);
                        
                        return (
                          <div
                            key={calDay.toISOString()}
                            className={`min-h-[100px] p-1 rounded border ${
                              isToday 
                                ? 'bg-primary/10 border-primary' 
                                : isCurrentMonth 
                                  ? 'bg-background border-border' 
                                  : 'bg-muted/30 border-border/50'
                            }`}
                          >
                            <div className={`text-sm font-medium mb-1 ${
                              isToday 
                                ? 'text-primary' 
                                : isCurrentMonth 
                                  ? 'text-foreground' 
                                  : 'text-muted-foreground'
                            }`}>
                              {format(calDay, 'd')}
                            </div>
                            <div className="space-y-0.5 max-h-[80px] overflow-y-auto">
                              {dayEvents.map(event => {
                                const title = getEventTitle(event);
                                const body = getEventBody(event);
                                const info = parseEventDesc(body);
                                const isTG = title?.includes('TG da web');
                                const displayTitle = isTG ? title : (title || 'Evento');
                                return (
                                  <div
                                    key={event.id}
                                    onClick={() => openEventDialog(event)}
                                    className={`text-[10px] p-1 rounded cursor-pointer ${
                                      title?.includes('CONFERMATO')
                                        ? 'bg-green-500 text-white hover:bg-green-600'
                                        : title?.includes('ATTESA')
                                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                                        : title?.includes('FATTO')
                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                        : 'bg-purple-500 text-white hover:bg-purple-600'
                                    }`}
                                    title={`${title}\n${info.cliente || ''}\n${info.modello || ''}\n${info.km ? 'KM: ' + info.km : ''}`}
                                  >
                                    <div className="font-medium truncate">
                                      {displayTitle}
                                    </div>
                                    {isTG && info.cliente && (
                                      <div className="truncate opacity-90 text-[9px]">
                                        {info.cliente}
                                      </div>
                                    )}
                                    {isTG && (info.modello || info.km) && (
                                      <div className="truncate opacity-90 text-[9px]">
                                        {info.modello}{info.modello && info.km ? ' • ' : ''}{info.km ? `${info.km} km` : ''}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Detail Dialog */}
      <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Dettagli Prenotazione
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (() => {
            const title = getEventTitle(selectedEvent);
            const body = getEventBody(selectedEvent);
            const info = parseEventDescription(body);
            const isWaiting = title?.includes('ATTESA');
            const isConfirmed = title?.includes('CONFERMATO');
            const startStr = getEventStart(selectedEvent);
            const endStr = getEventEnd(selectedEvent);
            
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium">{title?.replace('TG da web - ', '')}</span>
                  {getStatusBadge(selectedEvent)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {startStr && format(parseISO(startStr), 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>
                      Fine: {endStr && format(parseISO(endStr), 'HH:mm')}
                    </span>
                  </div>
                </div>
                
                <div className="border-t pt-4 space-y-2">
                  {info.cliente && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{info.cliente}</span>
                    </div>
                  )}
                  {info.telefono && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${info.telefono}`} className="text-primary hover:underline">{info.telefono}</a>
                    </div>
                  )}
                  {info.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${info.email}`} className="text-primary hover:underline">{info.email}</a>
                    </div>
                  )}
                  {info.modello && (
                    <div className="flex items-center gap-2">
                      <Bike className="w-4 h-4 text-muted-foreground" />
                      <span>{info.modello}</span>
                    </div>
                  )}
                  {info.km && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">KM:</span>
                      <span>{info.km}</span>
                    </div>
                  )}
                  {info.note && info.note !== 'Nessuna' && (
                    <div className="bg-muted p-2 rounded text-sm">
                      <span className="text-muted-foreground">Note:</span> {info.note}
                    </div>
                  )}
                </div>
                
                <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
                  {isWaiting && (
                    <Button 
                      onClick={handleConfirm} 
                      disabled={isProcessing}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Conferma
                    </Button>
                  )}
                  {isConfirmed && (
                    <Button 
                      onClick={openCompleteDialog}
                      disabled={isProcessing}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Fatto
                    </Button>
                  )}
                  {(isWaiting || isConfirmed) && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => setIsRescheduleDialogOpen(true)}
                        disabled={isProcessing}
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Riprogramma
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => setIsCancelDialogOpen(true)}
                        disabled={isProcessing}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Annulla
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="ghost"
                    onClick={() => window.open(getWebLink(selectedEvent), '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Apri in Outlook
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Annulla Prenotazione
            </DialogTitle>
            <DialogDescription>
              Il cliente riceverà un'email con il motivo dell'annullamento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Motivo annullamento</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Es: Indisponibilità del personale"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCancelDialogOpen(false)}>
              Indietro
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={isProcessing}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Conferma Annullamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Riprogramma Prenotazione
            </DialogTitle>
            <DialogDescription>
              Il cliente riceverà un'email con la nuova data/ora
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nuova Data</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ora Consegna</Label>
                <Input
                  type="time"
                  value={newStartTime}
                  onChange={(e) => setNewStartTime(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Ora Ritiro</Label>
                <Input
                  type="time"
                  value={newEndTime}
                  onChange={(e) => setNewEndTime(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsRescheduleDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleReschedule}
              disabled={isProcessing || !newDate || !newStartTime || !newEndTime}
            >
              {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Conferma Modifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete (FATTO) Dialog */}
      <Dialog open={isCompleteDialogOpen} onOpenChange={setIsCompleteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <CheckCircle2 className="w-5 h-5" />
              Completa Tagliando
            </DialogTitle>
            <DialogDescription>
              Carica la foto della bolla lavori e inserisci i dettagli
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Bolla upload */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4" />
                Foto Bolla Lavori
              </Label>
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBollaChange}
                  className="hidden"
                  id="bolla-upload-calendar"
                />
                <label htmlFor="bolla-upload-calendar" className="cursor-pointer block">
                  {bollaPreview ? (
                    <div className="space-y-2">
                      <img 
                        src={bollaPreview} 
                        alt="Anteprima bolla" 
                        className="max-h-40 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-muted-foreground">Clicca per cambiare</p>
                    </div>
                  ) : (
                    <div className="py-4">
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Clicca per caricare la foto della bolla
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        L'AI estrarrà automaticamente i lavori effettuati
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            {/* Lavori effettuati */}
            <div>
              <Label>Lavori Effettuati (opzionale)</Label>
              <Textarea
                value={lavoriEffettuati}
                onChange={(e) => setLavoriEffettuati(e.target.value)}
                placeholder="Es: Cambio olio, controllo freni, sostituzione pastiglie..."
                className="mt-1"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se carichi la foto della bolla, i lavori verranno estratti automaticamente
              </p>
            </div>
            
            {/* Note negozio */}
            <div>
              <Label>Note Aggiuntive (opzionale)</Label>
              <Textarea
                value={noteNegozio}
                onChange={(e) => setNoteNegozio(e.target.value)}
                placeholder="Note interne o per il cliente..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => {
                setIsCompleteDialogOpen(false);
                setBollaFile(null);
                setBollaPreview(null);
                setLavoriEffettuati('');
                setNoteNegozio('');
              }}
            >
              Annulla
            </Button>
            <Button 
              onClick={handleComplete}
              disabled={isUploadingBolla}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isUploadingBolla ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Completamento...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completa Tagliando
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Overlay for bolla upload */}
      <LoadingOverlay 
        isVisible={isUploadingBolla}
        message="Elaborazione bolla lavori..."
        submessage="Stiamo estraendo l'elenco dei lavori con l'AI"
      />
    </>
  );
};

export default OutlookCalendarView;
